import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'

const FILE_WAYS = [
  [UploadFileRoundedIcon, 'Upload for each unit', 'Use “Add unit evidence” to upload each unit’s document. You can do several at once. Per-unit evidence lives on the unit, not the building.'],
  [MailOutlineRoundedIcon, 'Email it to the unit', 'Every unit has its own email address (shown on the unit’s page). Send the document there and it’s filed to that unit automatically and matched to this requirement.'],
  [IosShareRoundedIcon, 'Share a building document', 'If a document already on the building applies to a unit, share it with that unit and it counts as that unit’s evidence.'],
]

const TASK_WAYS = [
  [AssignmentTurnedInRoundedIcon, 'Create the task across units', 'Create the task and apply it to all units. One task per unit. Completing each unit’s task covers that unit.'],
  [AssignmentTurnedInRoundedIcon, 'It’s met by doing, not a file', 'A completed task (or a resolved remedial) on a unit satisfies that unit. Uploading a file alone won’t meet a task-type requirement.'],
]

const PerUnitSatisfyExplainer = ({ taskKind = false, label = 'How do I cover all units?' }) => {
  const [open, setOpen] = useState(false)
  const ways = taskKind ? TASK_WAYS : FILE_WAYS

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <Wrap>
      <InfoButton
        type='button'
        aria-label='How do I satisfy a per-unit obligation?'
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(true) }}
      >
        <InfoOutlinedIcon style={{ fontSize: 14 }} />
        <span>{label}</span>
      </InfoButton>

      {open && createPortal(
        <Backdrop onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
          <Dialog role='dialog' aria-modal='true' aria-label='How to cover every unit'>
            <Header>
              <HeaderTitle>How to cover every unit</HeaderTitle>
              <CloseBtn type='button' aria-label='Close' onClick={() => setOpen(false)}>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </Header>

            <Body>
              <Lead>
                This requirement applies to <strong>each unit</strong>, not once for the whole building. It’s met
                when every applicable unit has its own current evidence{taskKind ? ' or completed task' : ''}. The
                bar shows how many units are covered so far.
              </Lead>

              <WhyHead>{taskKind ? 'How to cover a unit' : 'Three ways to cover a unit'}</WhyHead>
              <List>
                {ways.map(([Icon, h, t]) => (
                  <RowItem key={h}>
                    <IconTile><Icon style={{ fontSize: 16 }} /></IconTile>
                    <Text><strong>{h}.</strong> {t}</Text>
                  </RowItem>
                ))}
              </List>

              <Rule>
                {taskKind ? (
                  <>
                    <strong>When does a unit count?</strong> A per-unit <em>task</em> obligation is self-evidencing —
                    a unit turns green the moment its task is completed; no separate approval step is needed. The
                    obligation is satisfied once every applicable unit’s task is done.
                  </>
                ) : (
                  <>
                    <strong>When does a unit count?</strong> A per-unit <em>document</em> obligation needs a reviewer
                    to approve each unit’s file. New evidence shows <em>awaiting review</em> and does not count until
                    it is approved: <strong>approving one unit does not approve the others</strong>, so every unit is
                    checked on its own (GOV.UK-synced EPCs are approved automatically). A unit turns green once it has
                    current, approved evidence, and the obligation is satisfied when all applicable units are covered.
                  </>
                )}
              </Rule>
            </Body>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </Wrap>
  )
}

export default PerUnitSatisfyExplainer

const Wrap = styled.span` display: inline-flex; `

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 3px 10px 3px 7px;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100100;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff; border-radius: 16px;
  width: 620px; max-width: 100%; max-height: 88vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const Header = styled.div`
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 20px; border-bottom: 1px solid #f0f0f2;
`

const HeaderTitle = styled.h4`
  margin: 0; font-size: 16px; font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  font: inherit; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px; cursor: pointer; opacity: 0.7;
  &:hover { opacity: 1; background: rgba(12, 18, 41, 0.05); }
`

const Body = styled.div` overflow-y: auto; overflow-x: hidden; padding: 18px 20px 22px; `

const Lead = styled.p`
  margin: 0 0 4px; font-size: 13px; line-height: 1.55; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const WhyHead = styled.div`
  margin: 18px 0 10px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #8a909b;
`

const List = styled.div` display: flex; flex-direction: column; gap: 12px; `

const RowItem = styled.div` display: flex; gap: 11px; align-items: flex-start; `

const IconTile = styled.span`
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 8px;
  background: #eef4ff; color: #33becc;
`

const Text = styled.span`
  font-size: 12.5px; line-height: 1.5; color: #4d4d56; padding-top: 3px;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Rule = styled.p`
  margin: 18px 0 0; padding: 11px 13px; border-radius: 10px;
  background: #f6fbfa; border: 1px solid #e6f3f1;
  font-size: 12.5px; line-height: 1.5; color: #2f4f49;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
  em { font-style: normal; font-weight: 800; }
`
